import { ComponentType, ReactNode, SVGProps } from 'react';
import clsx from 'clsx';

export type ChipVariant =
    | 'neutral'
    | 'info'
    | 'success'
    | 'success-solid'
    | 'action'
    | 'danger'
    | 'outline'
    | 'outline-info';

export type IconType = ComponentType<SVGProps<SVGSVGElement>>;

const VARIANT: Record<ChipVariant, string> = {
    neutral: 'bg-tone-neutral-bg text-tone-neutral-fg',
    info: 'bg-tone-info-bg text-tone-info-fg',
    success: 'bg-tone-success-bg text-tone-success-fg',
    'success-solid': 'bg-tone-success-solid-bg text-tone-success-solid-fg',
    action: 'bg-tone-warning-bg text-tone-warning-fg',
    danger: 'bg-tone-danger-bg text-tone-danger-fg',
    outline: 'bg-surface text-body shadow-control',
    'outline-info': 'bg-surface text-link shadow-control-info',
};

export interface ChipProps {
    variant?: ChipVariant;
    size?: 'md' | 'lg';
    icon?: IconType;
    children: ReactNode;
    className?: string;
}

/** Static label. For something clickable use FilterChip. */
export const Chip = ({ variant = 'neutral', size = 'md', icon: Icon, children, className }: ChipProps) => (
    <span
        className={clsx(
            'inline-flex items-center gap-1.5 whitespace-nowrap rounded-chip',
            size === 'md' ? 'h-7 px-2.5 text-micro' : 'h-8 px-3 text-caption font-medium',
            VARIANT[variant],
            className
        )}
    >
        {Icon && <Icon className="size-4 shrink-0" aria-hidden="true" />}
        {children}
    </span>
);

export interface FilterChipProps {
    pressed: boolean;
    onToggle: () => void;
    count?: number;
    children: ReactNode;
}

/** Interactive filter: a `button[aria-pressed]`, 36 px visual, 44 px hit area. */
export const FilterChip = ({ pressed, onToggle, count, children }: FilterChipProps) => (
    <button
        type="button"
        aria-pressed={pressed}
        onClick={onToggle}
        className={clsx(
            'relative inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-chip px-3 text-small',
            'transition-[color,background-color,transform] duration-hover ease-snap active:scale-[0.97] active:duration-press',
            'after:absolute after:-inset-1 after:content-[""]',
            pressed ? 'bg-inverse text-on-inverse' : 'bg-surface text-strong shadow-control hover:bg-sunken'
        )}
    >
        {children}
        {count !== undefined && <span className="tabular-nums">{count}</span>}
    </button>
);
